import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Link2, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CopyLine, startEbayConnect, ebayEnvLabel } from "@/components/ebay/EbaySetup";

const Ok = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 text-xs font-semibold ${ok ? "text-emerald-700" : "text-amber-700"}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}{children}</span>
);

function EnvBlock({ env, st, onTest, testing, result }) {
  const e = st.environments[env];
  const accs = st.accounts.filter((a) => a.environment === env);
  const connect = () => startEbayConnect(env, "/settings/integrations");
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-2" data-testid={`ebay-env-${env}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold text-slate-900 capitalize">{env}</h3>
        <Ok ok={e.configured}>{e.configured ? "Keys saved" : e.has_keys ? "RuName missing" : "Keys missing"}</Ok>
        <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
          <Button size="sm" variant="outline" disabled={!e.has_keys || testing} onClick={() => onTest(env)} data-testid={`ebay-test-${env}`}>
            {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}Test connection</Button>
          <Button size="sm" disabled={!e.configured} onClick={connect} data-testid={`ebay-connect-${env}`}><Link2 className="w-4 h-4 mr-1" />Connect {ebayEnvLabel(env)} account</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">{accs.length ? accs.map((a) => `${a.label || a.ebay_username} (${ebayEnvLabel(a.environment)} · ${a.status})`).join(", ") : "No seller account connected."}</p>
      {result && (
        <div className="rounded-lg bg-slate-50 p-2.5 text-xs space-y-1" data-testid={`ebay-test-result-${env}`}>
          <Ok ok={result.keys.ok}>{result.keys.detail}</Ok>
          {result.accounts.map((a) => (
            <div key={a.id}><b>{a.username || a.label}</b>: <Ok ok={a.token}>token</Ok> · <Ok ok={a.identity}>identity</Ok> · <Ok ok={a.seller_privileges}>seller permissions</Ok>{a.detail && ` — ${a.detail}`}</div>))}
          {result.keys.ok && !result.accounts.length && <div className="text-slate-500">Connect a seller account to test its permissions.</div>}
        </div>)}
    </div>
  );
}

export function EbayIntegrationCard() {
  const [st, setSt] = useState(null);
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});
  const [params] = useSearchParams();
  const load = useCallback(() => api.get("/ebay/status").then((r) => setSt(r.data)).catch(() => setSt(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const c = params.get("ebay_connected");
    if (c === "1") { toast.success("eBay seller account connected (read-only)"); } else if (c === "0") { toast.error("eBay sign-in was cancelled or failed"); } else if (c === "taken") { toast.error("That eBay account is already connected to another company"); }
  }, [params]);
  const test = async (env) => {
    setTesting(env);
    try { const r = await api.post("/ebay/test", { environment: env }); setResults((x) => ({ ...x, [env]: r.data })); }
    catch (x) { toast.error(x.response?.data?.detail || "Test failed"); } finally { setTesting(null); }
  };
  if (st === false || st === null) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-4" data-testid="ebay-integration-card">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-bold text-slate-900">eBay</h2>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800" data-testid="ebay-test-mode">Test mode · read-only</span>
        <span className="text-xs text-slate-500">New connections use: <b className="capitalize">{st.environment}</b></span>
      </div>
      <p className="text-sm text-slate-500">No listings are published, no orders imported and no stock is changed. TedOps only reads from eBay.</p>
      <div className="grid gap-3 md:grid-cols-2">
        {["sandbox", "production"].map((env) => <EnvBlock key={env} env={env} st={st} onTest={test} testing={testing === env} result={results[env]} />)}
      </div>
      <div className="space-y-1" data-testid="ebay-portal-values">
        <h3 className="text-sm font-bold text-slate-900">Enter these in the eBay Developer Portal</h3>
        <CopyLine label="Auth accepted URL" value={st.urls.callback} testid="ebay-url-accepted" />
        <CopyLine label="Auth declined URL" value={st.urls.declined} testid="ebay-url-declined" />
        <CopyLine label="Deletion endpoint" value={st.urls.deletion} testid="ebay-url-deletion" />
        {st.deletion_token && <CopyLine label="Verification token" value={st.deletion_token} testid="ebay-deletion-token" />}
        <p className="text-xs text-slate-500 pt-1">OAuth scopes requested: {st.scopes.map((s) => s.split("/").pop()).join(", ")}</p>
      </div>
    </section>
  );
}
